/**
 * Hand a finished PNG to the phone's share sheet, or download it. navigator.share only works
 * inside a user gesture, and building a PNG takes long enough to lose that gesture, so the rule is:
 * build the Blob ahead (when the screen shows), then call this straight from the tap handler.
 * Games' share card, the sessions recap and the player card all use it.
 */
export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled';

export async function shareImage(blob: Blob, fileName: string): Promise<ShareOutcome> {
  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  if (typeof navigator !== 'undefined' && navigator.canShare?.({ files: [file] })) {
    try {
      // Files only: some iOS targets take the link instead when a URL is attached.
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
      // Any other failure falls through to the download.
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}
